// ============================================================================
// FarmerEnrollmentTab — producer enrollment status (Build S4, Task #25).
// ============================================================================
// Route: #my/enrollment. Shows the producer's enrollment(s) with the
// eligibility checklist as ACTIONABLE items — each missing artifact links to
// the surface that resolves it (sign the agreement -> #my/signatures; upload
// docs -> #my/documents). Read-only against /api/farmer/enrollments.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertCircle, ClipboardList, CheckCircle2, XCircle, MinusCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { api, type FarmerEnrollment, type EligibilitySnapshot } from '../api';
import { useFarmerAuth } from '../farmerAuth';

const STATUS_COPY: Record<string, string> = {
  invited: 'Your enrollment is being finalized by Up in Farms.',
  application_started: 'Your application is underway.',
  application_submitted: 'Your application has been submitted and is awaiting review.',
  under_review: 'Up in Farms is reviewing your application.',
  conditionally_approved: 'Approved pending final items — see the checklist below.',
  enrolled: 'You are enrolled. Welcome to the program!',
  withdrawn: 'This enrollment was withdrawn.',
  terminated: 'This enrollment was ended.',
  completed: 'This enrollment is complete.',
};

const REQUIREMENT_ACTIONS: Record<string, { label: string; href: string | null; cta: string | null }> = {
  AD_2047:  { label: 'AD-2047 customer data worksheet', href: '#my/documents', cta: 'View your documents' },
  AD_1026:  { label: 'AD-1026 HELC/WC certification',   href: '#my/documents', cta: 'View your documents' },
  W9:       { label: 'IRS W-9',                          href: '#my/documents', cta: 'View your documents' },
  AMP_AGREEMENT_EXECUTED: { label: 'Signed AMP Producer Participation Agreement', href: '#my/signatures', cta: 'Go to Signatures to review and sign' },
  CPA52_SIGNED: { label: 'Signed CPA-52', href: null, cta: null },
};

export function FarmerEnrollmentTab() {
  const { token: authToken } = useFarmerAuth();
  const token = authToken ?? '';
  const [enrollments, setEnrollments] = useState<FarmerEnrollment[] | null>(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<Record<string, EligibilitySnapshot | null>>({});

  const refresh = useCallback(async () => {
    if (!token) return;
    setError('');
    try {
      const res = await api.farmerEnrollments(token);
      setEnrollments(res.enrollments);
      if (res.enrollments.length === 1) setOpenId(res.enrollments[0].enrollment_id);
    } catch (e: any) {
      setError(e?.message || 'Could not load your enrollment');
    }
  }, [token]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    if (!openId || !token || snapshots[openId] !== undefined) return;
    void (async () => {
      try {
        const res = await api.farmerEnrollmentDetail(token, openId);
        setSnapshots((prev) => ({ ...prev, [openId]: res.snapshot }));
      } catch {
        setSnapshots((prev) => ({ ...prev, [openId]: null }));
      }
    })();
  }, [openId, token, snapshots]);

  if (!token) {
    return <p className="text-sm text-gray-600 p-4">Please sign in to see your enrollment.</p>;
  }
  if (error) {
    return <div className="flex items-center gap-2 text-red-700 bg-red-50 rounded-lg p-4"><AlertCircle className="w-5 h-5 shrink-0" /> {error}</div>;
  }
  if (!enrollments) {
    return <div className="flex items-center gap-2 text-gray-500 p-6"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <ClipboardList className="w-6 h-6 text-green-700" />
        <h2 className="text-xl font-bold text-gray-900">Program enrollment</h2>
      </div>

      {enrollments.length === 0 ? (
        <p className="text-sm text-gray-600 bg-white rounded-xl border border-gray-200 p-6">
          No program enrollments yet. If you expected one, contact Up in Farms at support@upinfarms.com.
        </p>
      ) : enrollments.map((e) => {
        const open = openId === e.enrollment_id;
        const snapshot = snapshots[e.enrollment_id];
        const missing = snapshot?.requirements.filter((r) => r.applies && !r.is_satisfied) ?? [];
        return (
          <div key={e.enrollment_id} className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <button
              type="button"
              className="w-full p-4 flex items-start justify-between gap-3 text-left"
              onClick={() => setOpenId(open ? null : e.enrollment_id)}
              aria-expanded={open}
            >
              <div>
                <h3 className="font-semibold text-gray-900">
                  {e.program_name} — {e.year_no}
                  <span className="ml-2 text-sm font-normal text-gray-500">({e.farm_name})</span>
                </h3>
                <p className="mt-1 text-sm text-gray-600">{STATUS_COPY[e.status] ?? e.status}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-800">
                  {e.status.replace(/_/g, ' ')}
                </span>
                {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </div>
            </button>

            {open && (
              <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                {snapshot === undefined ? (
                  <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading checklist…</div>
                ) : snapshot === null ? (
                  <p className="text-sm text-gray-500">Checklist unavailable right now.</p>
                ) : (
                  <>
                    <h4 className="text-sm font-semibold text-gray-700">
                      Eligibility checklist{' '}
                      {snapshot.is_complete
                        ? <span className="text-green-700">— all set!</span>
                        : missing.length > 0
                          ? <span className="text-amber-700">— {missing.length} item{missing.length === 1 ? '' : 's'} to complete</span>
                          : null}
                    </h4>
                    <ul className="mt-2 space-y-2">
                      {snapshot.requirements.map((r) => {
                        const action = REQUIREMENT_ACTIONS[r.requirement_type] ?? { label: r.requirement_type, href: null, cta: null };
                        return (
                          <li key={r.requirement_type} className="flex items-start gap-2 text-sm">
                            {!r.applies ? (
                              <MinusCircle className="w-4 h-4 text-gray-300 mt-0.5 shrink-0" />
                            ) : r.is_satisfied ? (
                              <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                            ) : (
                              <XCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                            )}
                            <span className={r.applies ? 'text-gray-800' : 'text-gray-400'}>
                              {action.label}
                              {!r.applies && <span className="ml-1 text-xs italic">(not required this year)</span>}
                              {r.applies && !r.is_satisfied && action.href && (
                                <a href={action.href} className="ml-2 text-green-700 underline text-xs font-medium">{action.cta}</a>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
